// Phase 84.M.94 — Force gate cho rating proposals pending.
//
// Spec (user choice 5a, 6a): chỉ chặn ở /athlete/* routes, không nút đóng,
// popup tự bật mỗi khi vào trang cho tới khi user xử lý xong.
//
// v17.perf.auth-storm (02/2026) — HAR PROD cho thấy 10x fetch
// `/api/athlete/rating-proposals` mỗi phiên do Gate refetch trên MỌI
// route change + Banner cũng gọi độc lập (không share cache).
// Fix: chuyển sang React Query (queryKey ["athlete","rating-proposals"])
// để MỌI consumer share cache — StrictMode/route change không refetch.
// Polling 30s giữ nguyên (cần realtime pop-up), nhưng route change
// KHÔNG còn trigger network request nữa.
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./AuthContext";
import { athleteRatingProposals } from "../lib/athleteApi";
import PendingRatingProposalForceModal from "../components/athlete/personal/PendingRatingProposalForceModal";
import { isUploadActive } from "../lib/uploadActivity";

const POLL_INTERVAL_MS = 30_000;

// Shared query key — Banner + Gate + bất kỳ consumer nào cũng dùng key này
// để hit chung 1 cache entry (dedupe request).
export const RATING_PROPOSALS_QUERY_KEY = ["athlete", "rating-proposals"];

export function PendingRatingProposalGateProvider({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  const enabled = !loading && !!user?.id;

  const { data: proposals, refetch } = useQuery({
    queryKey: RATING_PROPOSALS_QUERY_KEY,
    enabled,
    queryFn: async () => {
      // Không fetch trong lúc user upload (tránh remount cascade).
      if (isUploadActive()) return [];
      try {
        const list = await athleteRatingProposals.listPending();
        return Array.isArray(list) ? list : [];
      } catch {
        return [];
      }
    },
    // v17.perf.har2 — Gate là OWNER duy nhất của polling network cho query key
    // này (Banner observer-only). `staleTime: Infinity` + `refetchInterval` →
    // chỉ interval tick trigger request, không có race với `staleTime` expire.
    staleTime: Infinity,
    refetchInterval: enabled ? POLL_INTERVAL_MS : false,
    refetchOnMount: false,           // Đổi route KHÔNG refetch (dedupe).
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const proposal = Array.isArray(proposals) && proposals.length ? proposals[0] : null;

  // Refresh sau khi user resolve xong (accept/counter/reject).
  useEffect(() => {
    if (!enabled) return undefined;
    return undefined;
  }, [enabled]);

  const onAthleteRoute = location.pathname.startsWith("/athlete");
  const onEscapePath = ["/login", "/register", "/auth/callback", "/logout"].some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
  );

  const open = !!proposal && onAthleteRoute && !onEscapePath && !loading;

  return (
    <>
      {children}
      <PendingRatingProposalForceModal
        open={open}
        proposal={proposal}
        onResolved={() => {
          // Trigger refetch để pull các proposal còn lại (nếu admin gửi nhiều).
          refetch();
        }}
      />
    </>
  );
}
